import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFloatingChat } from '../../Contexts/FloatingChatContext';
import { useBookmarkContext } from '../../Contexts/BookmarkProvider';
import ReviewHistoryContainer from '../../Components/buyer/ReviewHistoryContainer';
import ProductCard from '../../Components/buyer/ProductCard';
import ScrollableRow from '../../Components/utils/ScrollableRow';
import Dropdown from '../../Components/utils/Dropdown';
import { showToast } from '../../Components/utils/ShowToast';
import { useCart } from '../../Contexts/CartContext';
import axios from 'axios';
import Loader from '../../Components/utils/Loader';
import { LoginContext } from '../../Contexts/LoginContext';
import { formatCurrency, transformProduct, calculateDistance, feeCategoryOf, feePercentFrom, listingPoint } from '../../data/constants';
import { formatCityRegion } from '../../Components/utils/formatAddress';
import { FaBookmark, FaStore, FaMapMarkerAlt, FaStar, FaShoppingBag, FaComment, FaTruck, FaBox, FaShieldAlt, FaTag, FaEdit } from 'react-icons/fa';
import SEO from '../../Components/utils/SEO';
import { stripHtml, truncateText } from '../../utils/seoUtils';
import { SEO_SITE_NAME, SEO_SITE_URL } from '../../config/seoConfig';

const ProductDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { addToCart, cartItems, removeFromCart, cartActionLoading, taxRate } = useCart();
  const { isLogin, user, feesObj, currency, exchangeRate } = useContext(LoginContext);
  const { openFloatingChat, setHideForSellerOwnProduct } = useFloatingChat();
  const { addBookmark, removeBookmark, isBookmarked, actionLoading: bookmarkActionLoading } = useBookmarkContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [sellerProducts, setSellerProducts] = useState([]);
  const [sellerProductsLoading, setSellerProductsLoading] = useState(false);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  const [suggestedHasMore, setSuggestedHasMore] = useState(false);
  const [suggestedPage, setSuggestedPage] = useState(0);
  const SUGGESTED_PAGE_SIZE = 8;
  const [selectedPackage, setSelectedPackage] = useState(0);
  const [neededOn] = useState(''); // eslint-disable-line no-unused-vars
  const [needLater] = useState(false); // eslint-disable-line no-unused-vars
  const [minDate, setMinDate] = useState(''); // eslint-disable-line no-unused-vars
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedVariables, setSelectedVariables] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [unitPrice, setUnitPrice] = useState(0);
  const [selectedDeliveryType, setSelectedDeliveryType] = useState(null);
  const [sortOption, setSortOption] = useState('newest');
  const [ratingOption, setRatingOption] = useState(1);
  const [isInCart, setIsInCart] = useState(false);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [customMainImage, setCustomMainImage] = useState(null);
  const [selectedAddedServices, setSelectedAddedServices] = useState([]); // Array of { index, qty }
  const [b2bListing, setB2bListing] = useState(null);
  const [b2bSidebarTab, setB2bSidebarTab] = useState('wholesale');
  const [browserLoc, setBrowserLoc] = useState(null);

  const market = new URLSearchParams(location.search).get('market');
  const isB2B = market === 'b2b' || location.pathname.startsWith('/productDetail/b2b/');
  const isOwnProduct = product?.sellerDetails?.sellerId === user?.sellerId?._id;

  useEffect(() => {
    if (!setHideForSellerOwnProduct) return;
    setHideForSellerOwnProduct(false);
    return () => setHideForSellerOwnProduct(false);
  }, [setHideForSellerOwnProduct]);

  // Try browser geolocation when user location is not available
  useEffect(() => {
    const buyerLoc = user?.location;
    if (buyerLoc && buyerLoc.lat !== 0 && buyerLoc.lon !== 0) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setBrowserLoc({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => { },
      { timeout: 5000, maximumAge: 600000 }
    );
  }, [user?.location]);

  useEffect(() => {
    if (isB2B) return;
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      setB2bListing(null);
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/product/${id}`);
        const transformedProduct = transformProductData(data.product);
        setProduct(transformedProduct);
        setSelectedDeliveryType(transformedProduct.deliveryType[0]);
        initializePricing(transformedProduct);
        setLoading(false);
        if (transformedProduct?.sellerDetails?.sellerId !== user?.sellerId?._id) {
          axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/product/${id}/impression`).catch(() => { });
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching product');
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, isB2B]);

  useEffect(() => {
    if (!isB2B) return;
    let cancelled = false;
    const fetchB2b = async () => {
      setLoading(true);
      setError(null);
      setProduct(null);
      setB2bListing(null);
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/listings/${id}`);
        if (!cancelled) {
          setB2bListing(data.listing);
          const transformed = transformB2BListing(data.listing);
          setProduct(transformed);
          setSelectedDeliveryType('delivery');
          const moq = data.listing.moq || 1;
          setQuantity(moq);
          initializePricing(transformed);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Listing not found');
          setLoading(false);
        }
      }
    };
    fetchB2b();
    return () => { cancelled = true; };
  }, [id, isB2B]);

  useEffect(() => {
    if (product && location.state?.fromCart) {
      if (location.state.deliveryType) setSelectedDeliveryType(location.state.deliveryType);
      if (location.state.quantity) setQuantity(location.state.quantity);
      if (location.state.addedServices) setSelectedAddedServices(location.state.addedServices);
      
      if (location.state.selectedOptions?.variablePricing?.variables) {
        const vars = {};
        location.state.selectedOptions.variablePricing.variables.forEach(v => {
          if (v?.name && v?.selectedOption?.name) {
            vars[v.name] = v.selectedOption.name;
          }
        });
        setSelectedVariables(vars);
      }
      
      if (location.state.selectedOptions?.fixedPricing?.packageIndex !== undefined) {
         setSelectedPackage(location.state.selectedOptions.fixedPricing.packageIndex);
      }
    }
  }, [location.key, product]);

  useEffect(() => {
    if (isB2B || !product?.sellerDetails?.sellerId) return;
    const fetchSellerProducts = async () => {
      setSellerProductsLoading(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/seller/${product.sellerDetails.sellerId}`
        );
        const transformed = (data.products || []).filter((p) => p._id !== id).map(transformProduct);
        setSellerProducts(transformed);
      } catch (err) { setSellerProducts([]); }
      finally { setSellerProductsLoading(false); }
    };
    fetchSellerProducts();
  }, [isB2B, product?.sellerDetails?.sellerId, id]);

  useEffect(() => {
    if (!id) return;
    setSuggestedPage(0);
    setSuggestedProducts([]);

    if (isB2B) {
      // Fetch suggested B2B listings
      if (!b2bListing?.maincategory) return;
      const fetchSuggestedB2B = async () => {
        setSuggestedLoading(true);
        try {
          const { data } = await axios.get(
            `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/listings/suggested`,
            { params: { category: b2bListing.maincategory, subcategory: b2bListing.subcategory || undefined, exclude: id, limit: SUGGESTED_PAGE_SIZE, skip: 0 } }
          );
          setSuggestedProducts(data.listings || []);
          setSuggestedHasMore(data.hasMore ?? false);
        } catch (err) { setSuggestedProducts([]); setSuggestedHasMore(false); }
        finally { setSuggestedLoading(false); }
      };
      fetchSuggestedB2B();
      return;
    }

    // Non-B2B: fetch suggested regular products
    if (!product?.maincategory) return;
    const fetchSuggested = async () => {
      setSuggestedLoading(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/suggested`,
          { params: { category: product.maincategory, subcategory: product.subcategory || undefined, exclude: id, limit: SUGGESTED_PAGE_SIZE, skip: 0 } }
        );
        const transformed = (data.products || []).map(transformProduct);
        setSuggestedProducts(transformed);
        setSuggestedHasMore(data.hasMore ?? false);
      } catch (err) { setSuggestedProducts([]); setSuggestedHasMore(false); }
      finally { setSuggestedLoading(false); }
    };
    fetchSuggested();
  }, [isB2B, product?.maincategory, product?.subcategory, b2bListing?.maincategory, b2bListing?.subcategory, id]);

  const loadMoreSuggested = async () => {
    if (suggestedLoading || !suggestedHasMore) return;

    if (isB2B) {
      if (!b2bListing?.maincategory) return;
      setSuggestedLoading(true);
      try {
        const nextSkip = suggestedPage + SUGGESTED_PAGE_SIZE;
        const { data } = await axios.get(
          `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/listings/suggested`,
          { params: { category: b2bListing.maincategory, subcategory: b2bListing.subcategory || undefined, exclude: id, limit: SUGGESTED_PAGE_SIZE, skip: nextSkip } }
        );
        setSuggestedProducts(prev => [...prev, ...(data.listings || [])]);
        setSuggestedHasMore(data.hasMore ?? false);
        setSuggestedPage(nextSkip);
      } catch (err) { setSuggestedHasMore(false); }
      finally { setSuggestedLoading(false); }
      return;
    }

    if (!product?.maincategory) return;
    setSuggestedLoading(true);
    try {
      const nextSkip = suggestedPage + SUGGESTED_PAGE_SIZE;
      const { data } = await axios.get(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/suggested`,
        { params: { category: product.maincategory, subcategory: product.subcategory || undefined, exclude: id, limit: SUGGESTED_PAGE_SIZE, skip: nextSkip } }
      );
      const transformed = (data.products || []).map(transformProduct);
      setSuggestedProducts(prev => [...prev, ...transformed]);
      setSuggestedHasMore(data.hasMore ?? false);
      setSuggestedPage(nextSkip);
    } catch (err) { setSuggestedHasMore(false); }
    finally { setSuggestedLoading(false); }
  };

  const transformProductData = (dbProduct) => ({
    id: dbProduct._id,
    title: dbProduct.title,
    description: dbProduct.description,
    ingredients: dbProduct.ingredients,
    allergyInfo: dbProduct.allergyInfo,
    images: dbProduct.images,
    thumbnail: dbProduct.thumbnail,
    deliveryType: dbProduct.deliveryType,
    pricingType: dbProduct.pricingType,
    packages: dbProduct.packages,
    variablePricing: dbProduct.variablePricing,
    pickupLocation: formatPickupLocation(dbProduct.pickupLocation),
    rawPickupLocation: dbProduct.pickupLocation || null,
    shippingPolicy: dbProduct.shippingPolicy || null,
    maincategory: dbProduct.maincategory,
    subcategory: dbProduct.subcategory,
    discounts: dbProduct.discounts,
    cancellable: dbProduct.cancellable,
    productDetails: dbProduct.productDetails,
    stock: dbProduct.stock,
    saleDiscount: dbProduct.saleDiscount || null,
    bulkPricing: dbProduct.bulkPricing || [],
    addedServices: dbProduct.addedServices || [],
    buyerQuestions: dbProduct.buyerQuestions || [],
    estimatedDeliveryDays: dbProduct.estimatedDeliveryDays || null,
    sellingScope: dbProduct.sellingScope || 'local',
    listingType: dbProduct.listingType || 'product',
    serviceMode: dbProduct.serviceMode || '',
    sellerDetails: {
      sellerId: dbProduct.sellerId._id,
      userId: dbProduct.sellerId.userId._id,
      name: dbProduct.sellerId.displayName || dbProduct.sellerId.fullName,
      sellerRating: dbProduct.sellerId.rating,
      languages: dbProduct.sellerId.userId.languages ? dbProduct.sellerId.userId.languages.split(',') : [],
      contact: dbProduct.sellerId.userId.phone || 'Not provided',
      ordersCompleted: dbProduct.sellerId.productsSold,
      description: dbProduct.sellerId.description,
      profileImageUrl: dbProduct.sellerId.profileImage,
      buyerAddress: dbProduct.sellerId.userId?.buyerAddress || null,
      location: formatCityRegion(dbProduct.sellerId.sellerAddress) || formatCityRegion(dbProduct.sellerId.userId?.buyerAddress),
      sellerAddress: dbProduct.sellerId.sellerAddress || null,
      geoLocation: listingPoint(dbProduct) || null,
      reviews: dbProduct.sellerId.noOfReviews,
    },
    reviews: dbProduct.reviews.map((review) => ({
      id: review._id,
      title: dbProduct.title,
      sellerName: dbProduct.sellerId.displayName,
      reviewerName: review.userId.username,
      overallRating: review.overallRating,
      ratings: review.ratings,
      comment: review.comment,
      images: review.images,
      createdAt: review.createdAt,
    })),
  });

  const transformB2BListing = (listing) => {
    const seller = listing.b2bSellerId || {};
    const sortedTiers = [...(listing.priceTiers || [])].sort((a, b) => a.minQty - b.minQty);
    const basePrice = sortedTiers.length > 0 ? sortedTiers[0].price : 0;
    return {
      id: listing._id,
      title: listing.title,
      description: listing.description,
      ingredients: null,
      allergyInfo: null,
      images: listing.images || [],
      thumbnail: listing.thumbnail,
      deliveryType: ['delivery'],
      pricingType: 'variable',
      packages: [],
      variablePricing: {
        basePrice,
        variables: (listing.variations || []).map(v => ({
          name: v.name,
          options: v.options.map(o => ({
            name: o.label,
            price: o.priceAdjust || 0,
            image: o.image || null,
          })),
        })),
      },
      pickupLocation: '',
      rawPickupLocation: null,
      shippingPolicy: listing.shippingPolicy || null,
      maincategory: listing.maincategory,
      subcategory: listing.subcategory,
      discounts: null,
      cancellable: listing.cancellable,
      productDetails: listing.productDetails || [],
      stock: null,
      saleDiscount: listing.saleDiscount || null,
      bulkPricing: sortedTiers.map(t => ({ minQuantity: t.minQty, price: t.price })),
      addedServices: listing.addedServices || [],
      buyerQuestions: listing.buyerQuestions || [],
      estimatedDeliveryDays: listing.estimatedDeliveryDays || null,
      sellingScope: listing.sellingScope || 'international',
      listingType: listing.listingType || 'product',
      serviceMode: '',
      sellerDetails: {
        sellerId: seller.sellerId?._id || seller.sellerId || seller._id,
        userId: seller.userId,
        name: seller.companyName || 'Business',
        sellerRating: listing.rating || 0,
        languages: [],
        contact: '',
        ordersCompleted: seller.sellerId?.productsSold ?? seller.fulfilledOrders ?? 0,
        description: seller.sellerId?.description || '',
        profileImageUrl: seller.sellerId?.profileImage || '',
        buyerAddress: seller.userId?.buyerAddress || null,
        location: formatCityRegion(seller.businessAddress) || formatCityRegion(seller.sellerId?.sellerAddress) || formatCityRegion(seller.userId?.buyerAddress),
        sellerAddress: seller.sellerId?.sellerAddress || null,
        geoLocation: seller.sellerId?.location || null,
        reviews: listing.noOfReviews || 0,
      },
      reviews: (listing.reviews || []).map((review) => ({
        id: review._id,
        title: listing.title,
        sellerName: seller.companyName,
        reviewerName: review.userId?.username || 'Anonymous',
        overallRating: review.overallRating,
        ratings: review.ratings,
        comment: review.comment,
        images: review.images,
        createdAt: review.createdAt,
      })),
    };
  };

  const formatPickupLocation = (location) => {
    if (!location) return '';
    const parts = [
      location.address,
      location.city,
      location.state?.name,
      location.country?.name,
      location.zipCode || location.postalCode,
    ].filter((p) => p != null && String(p).trim() !== '');
    return parts.join(', ');
  };

  const distanceKm = useMemo(() => {
    // Only show distance for local listings; international don't need km
    if (product?.sellingScope !== 'local') return null;
    const sellerGeo = product?.sellerDetails?.geoLocation;
    const buyerGeo = (user?.location && user.location.lat !== 0 && user.location.lon !== 0)
      ? user.location
      : browserLoc;
    if (!sellerGeo || !buyerGeo ||
      (sellerGeo.lat === 0 && sellerGeo.lon === 0)) return null;
    const d = calculateDistance(buyerGeo.lat, buyerGeo.lon, sellerGeo.lat, sellerGeo.lon);
    return d < 1 ? '< 1' : d.toLocaleString();
  }, [product?.sellingScope, product?.sellerDetails?.geoLocation, user?.location, browserLoc]);

  const saleDiscountActive = useMemo(() => {
    if (!product?.saleDiscount || !product.saleDiscount.value) return null;
    const sd = product.saleDiscount;
    if (sd.endDate && new Date(sd.endDate) < new Date()) return null;
    return sd;
  }, [product?.saleDiscount]);

  const listingReviewSummary = useMemo(() => {
    const list = product?.reviews;
    if (!list?.length) return { avg: null, count: 0 };
    const sum = list.reduce((acc, r) => acc + (Number(r.overallRating) || 0), 0);
    return { avg: sum / list.length, count: list.length };
  }, [product?.reviews]);

  const scrollToListingReviews = () => {
    document.getElementById('product-detail-reviews')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getBaseUnitPrice = () => {
    if (!product) return 0;
    if (product.pricingType === 'fixed') {
      return product.packages[selectedPackage]?.options?.basePrice || 0;
    }
    let price = product.variablePricing?.basePrice || 0;
    Object.entries(selectedVariables).forEach(([variableName, optionName]) => {
      const variable = product.variablePricing?.variables?.find((v) => v.name === variableName);
      if (variable) {
        const option = variable.options.find((o) => o.name === optionName);
        if (option) price += option.price;
      }
    });
    return price;
  };

  const applySaleDiscount = (price) => {
    if (!saleDiscountActive) return price;
    if (saleDiscountActive.type === 'percentage') return price * (1 - saleDiscountActive.value / 100);
    return Math.max(0, price - saleDiscountActive.value);
  };

  const getVariableOptionsPremium = () => {
    if (!product || product.pricingType !== 'variable') return 0;
    let sum = 0;
    Object.entries(selectedVariables).forEach(([variableName, optionName]) => {
      const variable = product.variablePricing?.variables?.find((v) => v.name === variableName);
      if (variable) {
        const option = variable.options.find((o) => o.name === optionName);
        if (option) sum += Number(option.price) || 0;
      }
    });
    return sum;
  };

  const getMatchingBulkTier = (qty) => {
    if (!product?.bulkPricing?.length || product.listingType === 'service') return null;
    const tierPool = product.pricingType === 'fixed'
      ? product.bulkPricing.filter((t) => !t.packageName || t.packageName === product.packages[selectedPackage]?.name)
      : product.bulkPricing;
    if (!tierPool.length) return null;
    const sortedDesc = [...tierPool].sort((a, b) => b.minQuantity - a.minQuantity);
    const matched = sortedDesc.find((t) => qty >= t.minQuantity);
    return matched || null;
  };

  /**
   * Variable pricing: volume tier price replaces list base; variation premiums always add on top.
   * (Old Math.min(tier, base+options) dropped option premiums whenever the tier was cheaper.)
   * Fixed pricing: keep bulk tier vs package price comparison.
   */
  const getEffectiveUnitPrice = (qty) => {
    if (!product) return 0;

    if (product.pricingType === 'variable') {
      const optionPremium = getVariableOptionsPremium();
      const matchingTier = getMatchingBulkTier(qty);
      const listBase = product.variablePricing?.basePrice || 0;
      const tierUnit = matchingTier ? Number(matchingTier.price) || 0 : listBase;
      const combined = tierUnit + optionPremium;
      return applySaleDiscount(combined);
    }

    const base = getBaseUnitPrice();
    const matchingTier = getMatchingBulkTier(qty);
    const discountedBase = applySaleDiscount(base);
    if (matchingTier) {
      return Math.min(applySaleDiscount(Number(matchingTier.price) || 0), discountedBase);
    }
    return discountedBase;
  };

  /**
   * Services are booked, not carted: they are scheduled against a date and the
   * seller's availability, which does not survive sitting in a basket. The
   * cart endpoint refuses them too — this only keeps the button off the page.
   */
  const isServiceListing = product?.listingType === 'service';

  // The buyer fee band this listing falls into. The server re-derives the same
  // thing at checkout; this only keeps the estimate honest.
  const buyerFeePercent = useMemo(() => {
    if (!product) return Number(feesObj.buyer) || 0;
    const category = feeCategoryOf({ isB2B, listingType: product.listingType });
    return feePercentFrom(feesObj.buyerMatrix, category) || Number(feesObj.buyer) || 0;
  }, [product, isB2B, feesObj]);

  const computeDeliveryFee = (deliveryType, qty) => {
    if (deliveryType !== 'delivery') return 0;
    if (product?.listingType === 'service' && product?.serviceMode === 'online') return 0;

    const sp = product?.shippingPolicy;
    if (!sp || !sp.type) return 0;

    const orderQty = qty || quantity || 1;

    // Helper: compute fee from a data source (flat-level or region-level)
    const calcFee = (data) => {
      if (!data) return 0;
      if (data.free) return 0;
      const pType = sp.type;
      if (pType === 'byOrder') return Number(data.flatFee) || 0;
      if (pType === 'byUnit') return (Number(data.perUnitFee) || 0) * orderQty;
      if (pType === 'byMOQ') {
        const moqQty = Number(data.moqQty) || 0;
        const moqFlatFee = Number(data.moqFlatFee) || 0;
        const moqPerUnitAbove = Number(data.moqPerUnitAbove) || 0;
        if (orderQty <= moqQty) return moqFlatFee;
        return moqFlatFee + (orderQty - moqQty) * moqPerUnitAbove;
      }
      return 0;
    };

    // For international scope, resolve the buyer's region
    if (product?.sellingScope === 'international' && sp.regions) {
      const sellerAddress = product?.sellerDetails?.sellerAddress || {};
      const buyerAddress = user?.buyerAddress || {};
      const sellerCountry = (sellerAddress?.country?.name || '').toLowerCase();
      const buyerCountry = (buyerAddress?.country?.name || '').toLowerCase();
      const sellerRegion = (sellerAddress?.state?.name || '').toLowerCase();
      const buyerRegion = (buyerAddress?.state?.name || '').toLowerCase();

      if (sellerCountry && buyerCountry && sellerCountry !== buyerCountry) {
        return calcFee(sp.regions.worldwide);
      }
      if (sellerRegion && buyerRegion && sellerRegion === buyerRegion) {
        return calcFee(sp.regions.nearby);
      }
      return calcFee(sp.regions.withinCountry);
    }

    // Local scope — use top-level shippingPolicy values
    return calcFee(sp);
  };

  const addedServicesTotal = useMemo(() => {
    return selectedAddedServices.reduce((sum, entry) => {
      const svc = product?.addedServices?.[entry.index];
      if (!svc) return sum;
      const svcPrice = parseFloat(svc.price) || 0;
      const svcQty = entry.qty || 1;
      // svcQty IS the absolute count (for perUnit it defaults to productQty, buyer can override freely)
      // perOrder: fixed qty=1, no multiplier needed
      return sum + svcPrice * svcQty;
    }, 0);
  }, [selectedAddedServices, product?.addedServices]);

  const initializePricing = (prod) => {
    if (prod.pricingType === 'fixed' && prod.packages?.length > 0) {
      const base = prod.packages[0].options.basePrice;
      setUnitPrice(base);
      setTotalPrice(base * quantity);
    } else if (prod.pricingType === 'variable') {
      const base = prod.variablePricing?.basePrice || 0;
      setUnitPrice(base);
      setTotalPrice(base * quantity);
    }
  };

  const calculateTotalPrice = () => {
    const effectiveUnit = getEffectiveUnitPrice(quantity);
    setUnitPrice(effectiveUnit);
    setTotalPrice(effectiveUnit * quantity + addedServicesTotal);
  };

  const handlePackageSelect = (idx) => {
    if (product.pricingType !== 'fixed') return;
    setSelectedPackage(idx);
  };

  const handleVariableChange = (variableName, optionName) => {
    const nv = { ...selectedVariables };
    if (optionName && optionName !== '') nv[variableName] = optionName;
    else delete nv[variableName];
    setSelectedVariables(nv);
  };

  const toggleAddedService = (index) => {
    const svc = product?.addedServices?.[index];
    setSelectedAddedServices(prev => {
      const exists = prev.find(e => e.index === index);
      if (exists) return prev.filter(e => e.index !== index);
      // perUnit: default qty = current product quantity; perOrder: always fixed at 1
      const defaultQty = svc?.pricingMode === 'perUnit' ? (quantity || 1) : 1;
      return [...prev, { index, qty: defaultQty }];
    });
  };

  const updateAddedServiceQty = (index, newQty) => {
    const qty = Math.max(1, parseInt(newQty) || 1);
    setSelectedAddedServices(prev =>
      prev.map(e => e.index === index ? { ...e, qty } : e)
    );
  };

  const isServiceSelected = (index) => selectedAddedServices.some(e => e.index === index);
  const getServiceQty = (index) => selectedAddedServices.find(e => e.index === index)?.qty || 1;

  const validateDate = () => {
    if (needLater && !neededOn) {
      showToast('Please select a date you need the product', 'error');
      return false;
    }
    return true;
  };

  const hasAllVariableSelections = () => {
    if (!product || product.pricingType !== 'variable') return true;
    const vars = product.variablePricing?.variables || [];
    if (vars.length === 0) return true;
    return vars.every((v) => {
      const sel = selectedVariables[v.name];
      return sel != null && String(sel).trim() !== '';
    });
  };

  const buildSelectedOptions = () => {
    if (product.pricingType === 'fixed') {
      const selectedPkg = product.packages[selectedPackage];
      return {
        fixedPricing: {
          packageName: selectedPkg.name,
          packageIndex: selectedPackage,
          basePrice: selectedPkg.options.basePrice,
        },
      };
    } else if (product.pricingType === 'variable') {
      const variables = product.variablePricing.variables
        .map((variable) => {
          const selectedOptionName = selectedVariables[variable.name];
          if (selectedOptionName === undefined) return null;
          const option = variable.options.find((o) => o.name === selectedOptionName);
          return { name: variable.name, selectedOption: { name: selectedOptionName, price: option ? option.price : 0 } };
        }).filter(v => v !== null);
      return { variablePricing: { variables, basePrice: product.variablePricing.basePrice } };
    }
    return {};
  };

  const handleAddToCart = () => {
    if (isB2B && b2bListing && (
      b2bListing.listingMode === 'customization' ||
      (b2bListing.listingMode === 'both' && b2bSidebarTab === 'customization')
    )) {
      showToast('Customization requests cannot be added to the cart. Contact the supplier.', 'info');
      return;
    }
    if (isServiceListing) {
      showToast('Services are booked directly. Use "Book Now" to continue.', 'info');
      return;
    }
    if (!isLogin || !user) { showToast('Please login to add to cart', 'warning'); return; }
    if (!validateDate()) return;
    if (!hasAllVariableSelections()) {
      showToast('Please choose an option for each variation', 'warning');
      return;
    }
    const unitPx = getEffectiveUnitPrice(quantity);
    const cartItemPayload = {
      productId: product.id,
      quantity,
      selectedOptions: buildSelectedOptions(),
      price: unitPx,
      name: product.title,
      thumbnail: product.thumbnail,
      deliveryType: selectedDeliveryType,
      pricingType: product.pricingType,
      addedServices: selectedAddedServices.map(entry => ({ ...product.addedServices[entry.index], qty: entry.qty, index: entry.index })),
      addedServicesTotal,
      ...(isB2B ? { isB2B: true } : {}),
    };
    addToCart(cartItemPayload);
  };

  const handleBuyNow = () => {
    if (isB2B && b2bListing && (
      b2bListing.listingMode === 'customization' ||
      (b2bListing.listingMode === 'both' && b2bSidebarTab === 'customization')
    )) {
      return;
    }
    if (!isLogin || !user) {
      showToast('Please login to continue', 'warning');
      navigate('/signin', { state: { returnTo: isB2B ? `/productDetail/b2b/${id}` : `/productDetail/${id}` } });
      return;
    }
    if (!validateDate()) return;
    if (!hasAllVariableSelections()) {
      showToast('Please choose an option for each variation', 'warning');
      return;
    }
    const unitPx = getEffectiveUnitPrice(quantity);
    const itemSubtotal = unitPx * quantity + addedServicesTotal;
    const itemDeliveryFee = computeDeliveryFee(selectedDeliveryType);
    const itemServiceFee = (itemSubtotal + itemDeliveryFee) * (buyerFeePercent / 100);
    const itemTax = (itemSubtotal + itemDeliveryFee + itemServiceFee) * (taxRate / 100);
    const itemTotal = itemSubtotal + itemDeliveryFee + itemServiceFee + itemTax;

    const orderItems = [{
      productId: product.id, quantity,
      selectedOptions: buildSelectedOptions(),
      price: unitPx, name: product.title,
      thumbnail: product.images[currentImageIndex],
      deliveryType: selectedDeliveryType,
      pricingType: product.pricingType,
      addedServices: selectedAddedServices.map(entry => ({ ...product.addedServices[entry.index], qty: entry.qty, index: entry.index })),
      addedServicesTotal,
      summary: { price: unitPx, quantity, subtotal: itemSubtotal, deliveryFee: itemDeliveryFee, serviceFee: itemServiceFee, tax: itemTax, total: itemTotal },
      orderType: 'single', amountToPay: itemTotal,
      ...(isB2B ? { isB2B: true, b2bListingId: product.id } : {}),
    }];

    const aggregateSummary = { subtotal: itemSubtotal, deliveryFee: itemDeliveryFee, serviceFee: itemServiceFee, tax: itemTax, total: itemTotal };

    navigate('/checkoutPage', {
      state: {
        orderItems, aggregateSummary,
        pickupInfo: selectedDeliveryType === 'pickup' && product.rawPickupLocation
          ? { address: product.rawPickupLocation.address, city: product.rawPickupLocation.city, state: product.rawPickupLocation.state?.name, country: product.rawPickupLocation.country?.name }
          : null,
        sellerLocation: product.sellerDetails?.geoLocation || null,
        ...(isB2B ? { isB2B: true } : {}),
      },
    });
  };

  useEffect(() => {
    if (isB2B || !product) return;
    const today = new Date();
    const minDays = product?.pricingType === 'fixed'
      ? parseInt(product.packages[selectedPackage]?.options.timeToDeliver) || 0
      : parseInt(product?.variablePricing?.timeToDeliver) || 0;
    const min = new Date(today);
    min.setDate(min.getDate() + minDays);
    setMinDate(min.toISOString().split('T')[0]);
  }, [isB2B, product, selectedPackage]);

  useEffect(() => {
    if (!product) return;
    calculateTotalPrice();
  }, [product, selectedPackage, selectedVariables, quantity, selectedAddedServices]);

  // When product quantity changes, sync all selected perUnit added services to match
  useEffect(() => {
    if (!product) return;
    setSelectedAddedServices(prev =>
      prev.map(entry => {
        const svc = product?.addedServices?.[entry.index];
        if (svc?.pricingMode === 'perUnit') {
          return { ...entry, qty: quantity || 1 };
        }
        return entry;
      })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity]);

  useEffect(() => {
    if (product?.reviews) {
      let temp = [...product.reviews].filter(r => r.overallRating >= ratingOption);
      temp.sort((a, b) => sortOption === 'newest' ? new Date(b.createdAt) - new Date(a.createdAt) : new Date(a.createdAt) - new Date(b.createdAt));
      setFilteredReviews(temp);
    }
  }, [product, sortOption, ratingOption]);

  // Variation-aware cart match: only match the exact variation combo currently selected
  const cartItem = cartItems.find((item) => {
    if (String(item.productId) !== String(id)) return false;
    if (Boolean(item.isB2B) !== Boolean(isB2B)) return false;
    if (!product) return false;
    // Match on selectedOptions so different variations are treated as distinct cart rows
    const currentOpts = JSON.stringify(buildSelectedOptions());
    return JSON.stringify(item.selectedOptions) === currentOpts;
  });
  useEffect(() => {
    setIsInCart(!!cartItem);
  }, [cartItem]);
  useEffect(() => {
    if (isB2B || !product) return;
    setQuantity(cartItem ? cartItem.quantity : 1);
  }, [cartItem, isB2B, product]);

  if (loading) {
    return (
      <>
        <SEO title="Product" description="Loading listing" canonicalPath={isB2B ? `/productDetail/${id}?market=b2b` : `/productDetail/${id}`} noIndex />
        <Loader type="simpleMax" />
      </>
    );
  }
  if (error) return (
    <div className="productDetailError">
      <SEO title="Product not found" description="This listing could not be found." canonicalPath={isB2B ? `/productDetail/${id}?market=b2b` : `/productDetail/${id}`} noIndex />
      <p>{error}</p>
      {isB2B ? <Link to="/b2b/products-services">Back to B2B Products</Link> : <Link to="/products-services">Back to Products</Link>}
    </div>
  );

  const canonicalPath = isB2B ? `/productDetail/${id}?market=b2b` : `/productDetail/${id}`;
  const sellerName = product?.sellerDetails?.name || product?.sellerDetails?.displayName || SEO_SITE_NAME;
  const seoDescription = product?.description
    ? truncateText(stripHtml(product.description), 160)
    : 'View product details, pricing, and seller information on Chocowi.';
  const seoImage = product?.images?.[0] || product?.thumbnail || undefined;
  const offerCurrency = 'USD';
  const baseOfferPrice = product?.pricingType === 'fixed'
    ? product?.packages?.[0]?.options?.basePrice
    : product?.variablePricing?.basePrice;
  const b2bPrices = b2bListing?.priceTiers?.map((tier) => Number(tier.price)).filter((price) => Number.isFinite(price) && price > 0) || [];
  const schemaOffers = isB2B
    ? (b2bPrices.length > 0 ? {
      "@type": "AggregateOffer",
      lowPrice: Math.min(...b2bPrices),
      highPrice: Math.max(...b2bPrices),
      offerCount: b2bPrices.length,
      priceCurrency: offerCurrency,
      url: `${SEO_SITE_URL}${canonicalPath}`
    } : undefined)
    : (Number.isFinite(Number(baseOfferPrice)) ? {
      "@type": "Offer",
      price: Number(baseOfferPrice),
      priceCurrency: offerCurrency,
      availability: (product?.stock != null && product.stock <= 0)
        ? 'https://schema.org/OutOfStock'
        : 'https://schema.org/InStock',
      url: `${SEO_SITE_URL}${canonicalPath}`
    } : undefined);
  const schema = {
    "@context": "https://schema.org",
    "@type": product?.listingType === 'service' ? 'Service' : 'Product',
    name: product?.title,
    description: seoDescription,
    image: seoImage ? [seoImage] : undefined,
    sku: product?.id,
    brand: { "@type": "Brand", name: sellerName },
    offers: schemaOffers
  };

  const sortOptions = [{ value: 'newest', label: 'Newest' }, { value: 'oldest', label: 'Oldest' }];
  const ratingOptions = [{ value: 4, label: '4 stars & up' }, { value: 3, label: '3 stars & up' }, { value: 2, label: '2 stars & up' }, { value: 1, label: '1 star & up' }];
  const basePrice = getBaseUnitPrice();
  const isOutOfStock = product.stock != null && product.stock <= 0;

  const sellerObj = product.sellerDetails && {
    _id: product.sellerDetails.sellerId,
    displayName: product.sellerDetails.name,
    profileImage: product.sellerDetails.profileImageUrl,
    sellerAddress: product.sellerDetails.sellerAddress || {
      city: product.sellerDetails.buyerAddress?.city,
      country: { name: product.sellerDetails.buyerAddress?.country?.name || '' },
    },
    userId: { _id: product.sellerDetails.userId, languages: product.sellerDetails.languages?.join(', ') },
    productsSold: product.sellerDetails.ordersCompleted,
    rating: product.sellerDetails.sellerRating,
    noOfReviews: product.sellerDetails.reviews,
    description: product.sellerDetails.description,
  };

  const b2bCustomizationActive = isB2B && b2bListing && (
    b2bListing.listingMode === 'customization' ||
    (b2bListing.listingMode === 'both' && b2bSidebarTab === 'customization')
  );
  const showB2BBuyNow = isB2B && b2bListing && !b2bCustomizationActive;

  const b2bSellerIdForBookmark = b2bListing?.b2bSellerId?.sellerId?._id || b2bListing?.b2bSellerId?.sellerId;
  const b2bSellerBookmarkObj = b2bListing && b2bSellerIdForBookmark && {
    _id: b2bSellerIdForBookmark,
    displayName: b2bListing.b2bSellerId?.companyName,
    profileImage: b2bListing.b2bSellerId?.sellerId?.profileImage,
    sellerAddress: b2bListing.b2bSellerId?.sellerId?.sellerAddress || {
      city: b2bListing.b2bSellerId?.userId?.buyerAddress?.city,
      country: { name: b2bListing.b2bSellerId?.userId?.buyerAddress?.country?.name || '' },
    },
    userId: { _id: b2bListing.b2bSellerId?.userId, languages: '' },
    productsSold: b2bListing.b2bSellerId?.sellerId?.productsSold ?? b2bListing.b2bSellerId?.fulfilledOrders ?? 0,
    rating: b2bListing.rating || 0,
    noOfReviews: b2bListing.noOfReviews || 0,
    description: b2bListing.b2bSellerId?.sellerId?.description || '',
  };

  return (
    <div className="productDetailContainer">
      <SEO
        title={product?.title}
        description={seoDescription}
        canonicalPath={canonicalPath}
        image={seoImage}
        type="product"
        schema={schema}
      />
      {cartActionLoading && <Loader type="checkmate" />}

      <div className="productDetailPageWrapper">
        <div className="productDetailMain">
          <div className="productDetailMainTop">
            <div className="productDetailHeader">
              <nav className="productDetailBreadcrumb">
                <Link to="/">Home</Link>
                <span className="breadcrumbSep">/</span>
                <Link to={isB2B ? '/b2b/products-services' : '/products-services'}>{isB2B ? 'B2B Marketplace' : 'Products'}</Link>
                <span className="breadcrumbSep">/</span>
                <span className="breadcrumbCurrent">{product.maincategory}</span>
              </nav>
              <div className="productDetailHeaderRight">
                {isOwnProduct && (
                  <div className="productDetailSellerActions">
                    <button
                      type="button"
                      className="productDetailEditBtn"
                      onClick={() => navigate(isB2B ? `/createProduct/${id}?market=b2b` : `/createProduct/${id}`)}
                    >
                      <FaEdit size={14} /> Edit
                    </button>
                  </div>
                )}
                <Link to={product.sellerDetails.sellerId ? `/vendorDetail/${product.sellerDetails.sellerId}` : '#'} className="productDetailSellerPill">
                  {product.sellerDetails.profileImageUrl
                    ? <img src={product.sellerDetails.profileImageUrl} alt="Seller" className="productDetailSellerPillImg" />
                    : <div className="productDetailSellerPillImg" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--orangeBgLight)', color: 'var(--primaryCopper)', fontWeight: 700 }}><FaStore /></div>
                  }
                  <div className="productDetailSellerPillInfo">
                    <span className="productDetailSellerPillName">
                      {product.sellerDetails.name}
                      <span className="productDetailSellerPillOrders"> · {product.sellerDetails.ordersCompleted || 0} orders completed</span>
                    </span>
                    <span className="productDetailSellerPillLocation">
                      <FaMapMarkerAlt /> {product.sellerDetails.location}
                      {distanceKm !== null && <span className="pdDistanceBadge">{distanceKm} km away</span>}
                      {product.sellingScope === 'local' && (
                        <span className="pdScopeBadge pdScopeBadgeLocal">📍 Local</span>
                      )}
                      {product.sellingScope === 'international' && (
                        <span className="pdScopeBadge pdScopeBadgeIntl">🌍 International</span>
                      )}
                    </span>
                  </div>
                </Link>
              </div>
            </div>

            <h1 className="productDetailTitle">{product.title}</h1>

            <button
              type="button"
              className="productDetailTopReviewsSummary"
              onClick={scrollToListingReviews}
              aria-label="Scroll to reviews"
            >
              <FaStar className="productDetailTopReviewsSummaryStar" aria-hidden />
              <span className="productDetailTopReviewsSummaryRating">
                {listingReviewSummary.count > 0 ? listingReviewSummary.avg.toFixed(1) : '—'}
              </span>
              <span className="productDetailTopReviewsSummaryCount">
                ({listingReviewSummary.count} {listingReviewSummary.count === 1 ? 'review' : 'reviews'})
              </span>
            </button>

            {/* Sale Discount Banner */}
            {/* {saleDiscountActive && (
              <div className="pdSaleBanner">
                <FaTag />
                <span className="pdSaleText">
                  {saleDiscountActive.type === 'percentage' ? `${saleDiscountActive.value}% OFF` : `$${saleDiscountActive.value} OFF`}
                </span>
                {saleDiscountActive.endDate && (
                  <span className="pdSaleEnds">
                    <FaClock /> Ends {new Date(saleDiscountActive.endDate).toLocaleDateString()}
                  </span>
                )}
              </div>
            )} */}

            {/* Bulk Pricing Tiers */}
            {product.bulkPricing?.length > 0 && product.listingType !== 'service' && (
              <div className="pdBulkTiers">
                {product.bulkPricing
                  .filter(t => product.pricingType !== 'fixed' || !t.packageName || t.packageName === product.packages[selectedPackage]?.name)
                  .sort((a, b) => a.minQuantity - b.minQuantity).map((tier, i) => (
                    <div key={i} className={`pdBulkTier ${quantity >= tier.minQuantity ? 'active' : ''}`}>
                      <span className="pdBulkQty">{tier.minQuantity}+ pcs</span>
                      <span className="pdBulkPrice">{formatCurrency(tier.price, currency, exchangeRate)}</span>
                    </div>
                  ))}
              </div>
            )}

            {/* Images */}
            <div className="productDetailImageSection">
              <div className="productDetailImageContainer">
                <div className="mainImage">
                  <img
                    src={customMainImage || product.images[currentImageIndex]}
                    alt={customMainImage ? 'Selected Option' : `Product view ${currentImageIndex + 1}`}
                    className="productDetailImage"
                  />
                  {isOutOfStock && <div className="pdOutOfStockOverlay">Out of Stock</div>}
                  {product.images.length > 1 && !customMainImage && (
                    <>
                      <button className="prevImageBtn" onClick={() => setCurrentImageIndex(prev => prev > 0 ? prev - 1 : product.images.length - 1)}>‹</button>
                      <button className="nextImageBtn" onClick={() => setCurrentImageIndex(prev => (prev + 1) % product.images.length)}>›</button>
                      <div className="imageCounter">{currentImageIndex + 1} / {product.images.length}</div>
                    </>
                  )}
                </div>
                {product.images.length > 1 && (
                  <div className="imageThumbnails">
                    {product.images.map((img, idx) => (
                      <img key={idx} src={img} alt={`Thumbnail ${idx + 1}`}
                        className={`thumbnail ${currentImageIndex === idx ? 'active' : ''}`}
                        onClick={() => { setCurrentImageIndex(idx); setCustomMainImage(null); }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional info */}
          <div className="productAdditionalInfo">
            {product.maincategory === 'Food, Sweets & Drinks' ? (
              <>
                {product.ingredients && (
                  <div className="additionalInfoSection">
                    <h3>Main Ingredients</h3>
                    <p>{product.ingredients}</p>
                  </div>
                )}
                <div className="additionalInfoSection">
                  <h3>Allergy Information</h3>
                  <p>{product.allergyInfo || 'No allergy information provided'}</p>
                </div>
              </>
            ) : (
              product.productDetails?.length > 0 && (
                <div className="pdSpecsTable">
                  <h3>Specifications</h3>
                  <table>
                    <tbody>
                      {product.productDetails.map((detail, idx) => (
                        <tr key={idx}>
                          <td className="pdSpecLabel">{detail.title}</td>
                          <td className="pdSpecValue">{detail.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
            {isB2B && b2bListing?.featureTable?.length > 0 && (
              <div className="pdSpecsTable">
                <h3>Features</h3>
                <table>
                  <tbody>
                    {b2bListing.featureTable.map((f, idx) => (
                      <tr key={idx}>
                        <td className="pdSpecLabel">{f.label}</td>
                        <td className="pdSpecValue">{f.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}



            <div className="additionalInfoSection">
              <h3>Product Description</h3>
              <p>{product.description}</p>
            </div>
            <div className="additionalInfoMeta">
              <span><strong>Category:</strong> {product.maincategory}</span>
              {product.subcategory && <span><strong>Subcategory:</strong> {product.subcategory}</span>}
              {product.stock != null && product.listingType !== 'service' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FaBox />
                  <strong>Stock:</strong> {product.stock > 0 ? `${product.stock} available` : 'Out of stock'}
                </span>
              )}
              {product.listingType === 'service' && product.serviceMode && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <strong>Service Mode:</strong> {product.serviceMode === 'online' ? 'Online / Digital' : product.serviceMode === 'onsite' ? 'At Your Location' : 'At Seller Location'}
                </span>
              )}
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <strong>Selling Scope:</strong>
                {product.sellingScope === 'local'
                  ? <span className="pdScopeBadge pdScopeBadgeLocal">📍 Local</span>
                  : <span className="pdScopeBadge pdScopeBadgeIntl">🌍 International</span>
                }
              </span>
            </div>

            {!isB2B && product.buyerQuestions?.length > 0 && (
              <div className="additionalInfoSection">
                <h3>Requirements from Seller</h3>
                <p style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>You'll need to answer these before placing an order:</p>
                {product.buyerQuestions.map((q, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px', padding: '10px', background: '#f9fafb', borderRadius: '8px' }}>
                    {q.image && <img src={q.image} alt="Reference" style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px' }} />}
                    <p style={{ margin: 0 }}><strong>{idx + 1}.</strong> {q.question}</p>
                  </div>
                ))}
              </div>
            )}

            {product.sellingScope === 'international' && product.shippingPolicy?.regions && (
              <div className="pdShippingInfo">
                <h3><FaTruck style={{ marginRight: 6 }} /> Shipping Information</h3>
                <div className="pdShippingGrid">
                  {[
                    { key: 'nearby', label: 'Nearby / Regional', icon: '📍' },
                    { key: 'withinCountry', label: 'Within Country', icon: '🏠' },
                    { key: 'worldwide', label: 'Worldwide', icon: '🌍' },
                  ].map(tier => {
                    const regionData = product.shippingPolicy.regions[tier.key] || {};
                    const days = product.estimatedDeliveryDays?.[tier.key];
                    const pType = product.shippingPolicy.type || 'byOrder';
                    let feeLabel = '';
                    if (regionData.free) {
                      feeLabel = 'Free Shipping';
                    } else if (pType === 'byOrder') {
                      feeLabel = `+${formatCurrency(Number(regionData.flatFee) || 0, currency, exchangeRate)} per order`;
                    } else if (pType === 'byUnit') {
                      feeLabel = `+${formatCurrency(Number(regionData.perUnitFee) || 0, currency, exchangeRate)} per unit`;
                    } else if (pType === 'byMOQ') {
                      feeLabel = `${formatCurrency(Number(regionData.moqFlatFee) || 0, currency, exchangeRate)} up to ${regionData.moqQty || 0} units`;
                    }
                    return (
                      <div key={tier.key} className="pdShippingTier">
                        <span className="pdShippingTierIcon">{tier.icon}</span>
                        <div className="pdShippingTierInfo">
                          <span className="pdShippingTierLabel">{tier.label}</span>
                          <span className="pdShippingTierFee">
                            {regionData.free ? <span className="pdFreeShipping">Free Shipping</span> : feeLabel}
                          </span>
                          {days && <span className="pdShippingTierDays">{days}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Shipping Policy — how fees are calculated */}
            {/* {product.shippingPolicy?.type && product.listingType !== 'service' && (
              <div className="pdShippingInfo" style={{ marginTop: '16px' }}>
                <h3><FaTruck style={{ marginRight: 6 }} /> Shipping Fee Calculation</h3>
                {product.shippingPolicy.type === 'byOrder' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: '#f0f9ff', borderRadius: '10px', border: '1px solid #bae6fd' }}>
                    <span style={{ fontSize: '22px' }}>📦</span>
                    <div>
                      <strong style={{ fontSize: '14px', color: '#0369a1' }}>Flat Fee per Order</strong>
                      {product.shippingPolicy.flatFee != null && (
                        <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#555' }}>
                          {Number(product.shippingPolicy.flatFee) === 0 ? 'Free shipping on every order' : `${formatCurrency(product.shippingPolicy.flatFee, currency, exchangeRate)} per order`}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {product.shippingPolicy.type === 'byUnit' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: '#f0fff4', borderRadius: '10px', border: '1px solid #86efac' }}>
                    <span style={{ fontSize: '22px' }}>📏</span>
                    <div>
                      <strong style={{ fontSize: '14px', color: '#15803d' }}>Per Unit Shipping</strong>
                      {product.shippingPolicy.perUnitFee != null && (
                        <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#555' }}>
                          {formatCurrency(product.shippingPolicy.perUnitFee, currency, exchangeRate)} × quantity ordered
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {product.shippingPolicy.type === 'byMOQ' && (
                  <div style={{ padding: '12px', background: '#fffbeb', borderRadius: '10px', border: '1px solid #fcd34d' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '22px' }}>📊</span>
                      <strong style={{ fontSize: '14px', color: '#b45309' }}>MOQ-Based Shipping</strong>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#555' }}>
                      <span>Up to {product.shippingPolicy.moqQty} units: <strong>{formatCurrency(product.shippingPolicy.moqFlatFee, currency, exchangeRate)}</strong> flat</span>
                      <span>Above MOQ: <strong>+{formatCurrency(product.shippingPolicy.moqPerUnitAbove, currency, exchangeRate)}</strong>/unit</span>
                    </div>
                  </div>
                )}
              </div>
            )} */}

          </div>

          {/* Seller Card */}
          {isB2B && b2bListing ? (
            <div className="productDetailSellerCard">
              <div className="productDetailSellerCardInner">
                <Link to={b2bListing.b2bSellerId?.sellerId?._id ? `/vendorDetail/${b2bListing.b2bSellerId.sellerId._id}` : (b2bListing.b2bSellerId?.sellerId ? `/vendorDetail/${b2bListing.b2bSellerId.sellerId}` : '#')} className="productDetailSellerCardHeader">
                  {b2bListing.b2bSellerId?.sellerId?.profileImage ? (
                    <img src={b2bListing.b2bSellerId.sellerId.profileImage} alt={b2bListing.b2bSellerId?.companyName} className="productDetailSellerCardImage" />
                  ) : (
                    <div className="productDetailSellerCardImage" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--orangeBgLight)', color: 'var(--primaryCopper)', fontWeight: 700, fontSize: '20px', borderRadius: '50%' }}>
                      <FaStore />
                    </div>
                  )}
                  <div className="productDetailSellerCardInfo">
                    <h3 className="productDetailSellerCardName">{b2bListing.b2bSellerId?.companyName}</h3>
                    <p className="productDetailSellerCardLocation">
                      <FaMapMarkerAlt /> {product.sellerDetails.location || b2bListing.sellerCountry || '—'}
                    </p>
                    <div className="productDetailSellerCardRating">
                      <FaStar className="productDetailSellerCardStar" />
                      <span className="productDetailSellerCardRatingValue">{b2bListing.rating?.toFixed(1) || '0.0'}</span>
                      <span className="productDetailSellerCardReviews">({b2bListing.noOfReviews || 0} reviews) · {product.sellerDetails.ordersCompleted} orders completed</span>
                    </div>
                  </div>
                </Link>
                <div className="productDetailSellerCardMeta">
                  {b2bListing.b2bSellerId?.natureOfOrg && (
                    <span className="productDetailSellerMetaItem">Business Type: {b2bListing.b2bSellerId.natureOfOrg}</span>
                  )}
                  {b2bListing.b2bSellerId?.yearFounded && (
                    <span className="productDetailSellerMetaItem">Established: {b2bListing.b2bSellerId.yearFounded}</span>
                  )}
                  {b2bListing.b2bSellerId?.numberOfEmployees && (
                    <span className="productDetailSellerMetaItem">Employees: {b2bListing.b2bSellerId.numberOfEmployees}</span>
                  )}
                  {b2bListing.b2bSellerId?.productionCapacity && (
                    <span className="productDetailSellerMetaItem">Production Capacity: {b2bListing.b2bSellerId.productionCapacity}</span>
                  )}
                  {b2bListing.b2bSellerId?.inventoryCapacity && (
                    <span className="productDetailSellerMetaItem">Inventory Capacity: {b2bListing.b2bSellerId.inventoryCapacity}</span>
                  )}
                  {b2bListing.b2bSellerId?.exportMarkets?.length > 0 && (
                    <span className="productDetailSellerMetaItem">Export Markets: {b2bListing.b2bSellerId.exportMarkets.join(', ')}</span>
                  )}
                  {b2bListing.b2bSellerId?.certifications?.length > 0 && b2bListing.b2bSellerId.certifications.some(c => c.name) && (
                    <span className="productDetailSellerMetaItem">Certifications: {b2bListing.b2bSellerId.certifications.map(c => c.name).filter(Boolean).join(', ')}</span>
                  )}
                  {b2bListing.b2bSellerId?.buyerCountries?.length > 0 && (
                    <span className="productDetailSellerMetaItem">Buyer Countries: {b2bListing.b2bSellerId.buyerCountries.join(', ')}</span>
                  )}
                </div>
                {b2bListing.b2bSellerId?.sellerId?.description && (
                  <p className="productDetailSellerCardDesc">{b2bListing.b2bSellerId.sellerId.description}</p>
                )}
                <div className="productDetailSellerCardActions">
                  {(b2bListing.b2bSellerId?.sellerId?._id || b2bListing.b2bSellerId?.sellerId) && (
                    <Link to={`/vendorDetail/${b2bListing.b2bSellerId.sellerId?._id || b2bListing.b2bSellerId.sellerId}`} className="productDetailSellerViewProfile"><FaStore /> View Store</Link>
                  )}
                  {isLogin && b2bSellerBookmarkObj && (
                    <button type="button" className={`productDetailSellerBookmarkBtn ${isBookmarked(b2bSellerIdForBookmark) ? 'bookmarked' : ''}`}
                      onClick={() => isBookmarked(b2bSellerIdForBookmark) ? removeBookmark(b2bSellerIdForBookmark) : addBookmark(b2bSellerBookmarkObj)}
                      disabled={bookmarkActionLoading[b2bSellerIdForBookmark]}
                    >
                      {bookmarkActionLoading[b2bSellerIdForBookmark] ? <Loader type="miniPlus" /> : (<><FaBookmark /> {isBookmarked(b2bSellerIdForBookmark) ? 'Bookmarked' : 'Bookmark'}</>)}
                    </button>
                  )}
                  <button type="button" className="productDetailSellerContactBtn"
                    onClick={() => { if (!isLogin) { navigate('/signin', { state: { returnTo: `/productDetail/${id}?market=b2b` } }); return; } openFloatingChat(b2bListing.b2bSellerId?.userId?._id || b2bListing.b2bSellerId?.userId); }}
                  >
                    <FaComment /> Contact Supplier
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="productDetailSellerCard">
              <div className="productDetailSellerCardInner">
                <Link to={`/vendorDetail/${product.sellerDetails.sellerId}`} className="productDetailSellerCardHeader">
                  <img src={product.sellerDetails.profileImageUrl} alt={product.sellerDetails.name} className="productDetailSellerCardImage" />
                  <div className="productDetailSellerCardInfo">
                    <h3 className="productDetailSellerCardName">{product.sellerDetails.name}</h3>
                    <p className="productDetailSellerCardLocation">
                      <FaMapMarkerAlt /> {product.sellerDetails.location}
                      {distanceKm !== null && <span className="pdDistanceBadge">{distanceKm} km</span>}
                    </p>
                    <div className="productDetailSellerCardRating">
                      <FaStar className="productDetailSellerCardStar" />
                      <span className="productDetailSellerCardRatingValue">{product.sellerDetails.sellerRating?.toFixed(1) || '0.0'}</span>
                      <span className="productDetailSellerCardReviews">({product.sellerDetails.reviews} reviews) · {product.sellerDetails.ordersCompleted} orders completed</span>
                    </div>
                  </div>
                </Link>
                <div className="productDetailSellerCardMeta">
                  {product.sellerDetails.languages?.length > 0 && (
                    <span className="productDetailSellerMetaItem">Languages: {product.sellerDetails.languages.join(', ')}</span>
                  )}
                </div>
                {product.sellerDetails.description && (
                  <p className="productDetailSellerCardDesc">{product.sellerDetails.description}</p>
                )}
                <div className="productDetailSellerCardActions">
                  <Link to={`/vendorDetail/${product.sellerDetails.sellerId}`} className="productDetailSellerViewProfile"><FaStore /> View Store</Link>
                  {isLogin && (
                    <button type="button" className={`productDetailSellerBookmarkBtn ${isBookmarked(product.sellerDetails.sellerId) ? 'bookmarked' : ''}`}
                      onClick={() => isBookmarked(product.sellerDetails.sellerId) ? removeBookmark(product.sellerDetails.sellerId) : addBookmark(sellerObj)}
                      disabled={bookmarkActionLoading[product.sellerDetails.sellerId]}
                    >
                      {bookmarkActionLoading[product.sellerDetails.sellerId] ? <Loader type="miniPlus" /> : (<><FaBookmark /> {isBookmarked(product.sellerDetails.sellerId) ? 'Bookmarked' : 'Bookmark'}</>)}
                    </button>
                  )}
                  <button type="button" className="productDetailSellerContactBtn"
                    onClick={() => { if (!isLogin) { navigate('/signin', { state: { returnTo: `/productDetail/${id}` } }); return; } openFloatingChat(product.sellerDetails?.userId); }}
                  >
                    <FaComment /> Contact Seller
                  </button>
                </div>
              </div>
            </div>
          )}

          {sellerProducts.length > 0 && (
            <div className="productDetailRelatedSection">
              <h2 className="productDetailSectionTitle"><FaShoppingBag /> More from {product.sellerDetails.name}</h2>
              <ScrollableRow title="" items={sellerProducts} itemsPerRow={4} renderItem={(p) => <ProductCard key={p.id} product={p} />} isLoading={sellerProductsLoading} noItemsMessage="" />
            </div>
          )}

          <div className="productDetailReviewsSection" id="product-detail-reviews">
            <h2 className="productDetailSectionTitle">Reviews</h2>
            <div className="reviewsFilterContainer">
              <div className="filterOptions">
                <div className="defaultDropdown">
                  <label htmlFor="sortOption">Sort</label>
                  <Dropdown options={sortOptions} value={sortOptions.find(o => o.value === sortOption)} onChange={o => setSortOption(o.value)} placeholder="Sort" />
                </div>
                <div className="defaultDropdown">
                  <label htmlFor="ratingOption">Rating</label>
                  <Dropdown options={ratingOptions} value={ratingOptions.find(o => o.value === ratingOption)} onChange={o => setRatingOption(o.value)} placeholder="Rating" />
                </div>
              </div>
            </div>
            {filteredReviews.length > 0 ? (
              <ReviewHistoryContainer reviews={filteredReviews} isMyReview={false} isProductReview={true} />
            ) : (
              <p className="productDetailNoReviews">No reviews yet.</p>
            )}
          </div>
        </div>

        {/* ─── SIDEBAR ─────────────────────────────────── */}
        <aside className="productDetailSidebar">
          <div className="productDetailSidebarInner">
            <div className="productDetailSidebarScroll">
              {isB2B && b2bListing ? (
                <>
                  {/* B2B Sidebar Tabs */}
                  {b2bListing.listingMode === 'both' && (
                    <div className="packageOptions">
                      <div className="packageOptionsGrid">
                        <button className={`packageOption ${b2bSidebarTab === 'wholesale' ? 'active' : ''}`} onClick={() => setB2bSidebarTab('wholesale')}>Wholesale</button>
                        <button className={`packageOption ${b2bSidebarTab === 'customization' ? 'active' : ''}`} onClick={() => setB2bSidebarTab('customization')}>Customization</button>
                      </div>
                    </div>
                  )}

                  {/* Wholesale Content */}
                  {(b2bListing.listingMode !== 'customization' && (b2bListing.listingMode !== 'both' || b2bSidebarTab === 'wholesale')) && (
                    <>
                      {/* Price Section */}
                      <div className="priceInfo">
                        <p className="priceLabel">Price per unit</p>
                        <div className="pdPriceRow">
                          <h2 className="priceValue">{formatCurrency(unitPrice, currency, exchangeRate)}</h2>
                          {saleDiscountActive && unitPrice < basePrice && (
                            <span className="pdOriginalPrice">{formatCurrency(basePrice, currency, exchangeRate)}</span>
                          )}
                          {saleDiscountActive && (
                            <span className="pdDiscountBadge">
                              {saleDiscountActive.type === 'percentage' ? `${saleDiscountActive.value}% OFF` : `$${saleDiscountActive.value} OFF`}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Variable Selectors (Variations) */}
                      {product.variablePricing?.variables?.map((variable, idx) => (
                        <div key={idx} className="variableSelectorWrapper">
                          <p className="variableLabel">{variable.name}</p>
                          <div className="visualOptionsGrid">
                            {variable.options.map((opt, i) => {
                              const isSelected = selectedVariables[variable.name] === opt.name;
                              return (
                                <div key={i} className={`visualOptionChip ${isSelected ? 'active' : ''}`}
                                  onClick={() => { handleVariableChange(variable.name, opt.name); setCustomMainImage(opt.image || null); }}
                                >
                                  {opt.image && <img src={opt.image} alt={opt.name} className="chipImage" />}
                                  <div className="chipDetails">
                                    <span className="chipName">{opt.name}</span>
                                    <span className="chipPrice">{opt.price > 0 ? `+${formatCurrency(opt.price, currency, exchangeRate)}` : 'Included'}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}

                      {/* Quantity */}
                      <div className="quantitySelector">
                        <span className="quantityLabel">Quantity <span style={{ fontSize: '12px', color: '#6b7280' }}>(MOQ: {b2bListing.moq})</span></span>
                        <div className="quantityControls">
                          <button className="quantityBtn" onClick={() => quantity > b2bListing.moq && setQuantity(quantity - 1)} disabled={quantity <= b2bListing.moq}>−</button>
                          <input
                            type="text"
                            className="quantityInput"
                            value={quantity}
                            onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setQuantity(v === '' ? '' : parseInt(v)); }}
                            onBlur={() => { const min = b2bListing.moq || 1; setQuantity(prev => { const n = parseInt(prev); return (!n || n < min) ? min : n; }); }}
                          />
                          <button className="quantityBtn" onClick={() => setQuantity(quantity + 1)}>+</button>
                        </div>
                      </div>

                      {/* Bulk pricing hint */}
                      {product.bulkPricing?.length > 0 && (() => {
                        const sorted = [...product.bulkPricing].sort((a, b) => a.minQuantity - b.minQuantity);
                        const nextTier = sorted.find(t => quantity < t.minQuantity);
                        if (!nextTier) return null;
                        const tierPrice = getEffectiveUnitPrice(nextTier.minQuantity);
                        const currentPrice = getEffectiveUnitPrice(quantity);
                        const savings = currentPrice > 0 ? Math.round((1 - tierPrice / currentPrice) * 100) : 0;
                        return (
                          <p className="pdBulkHint">
                            <FaTag /> Buy {nextTier.minQuantity}+ for {formatCurrency(tierPrice, currency, exchangeRate)} each
                            {savings > 0 && <span style={{ marginLeft: 6, fontWeight: 600, color: '#16a34a' }}>({savings}% off)</span>}
                          </p>
                        );
                      })()}

                      {/* Added Services */}
                      {product.addedServices?.length > 0 && (
                        <div className="pdAddedServices">
                          <p className="pdAddedServicesTitle">Added Services</p>
                          {product.addedServices.map((svc, idx) => (
                            <div key={idx} className={`pdAddedServiceItem ${isServiceSelected(idx) ? 'active' : ''}`}>
                              <label className="pdAddedServiceMain">
                                <input type="checkbox" checked={isServiceSelected(idx)} onChange={() => toggleAddedService(idx)} />
                                {svc.image && <img src={svc.image} alt={svc.name} className="pdAddedServiceImg" />}
                                <div className="pdAddedServiceInfo">
                                  <span className="pdAddedServiceName">{svc.name}</span>
                                  {svc.description && <span className="pdAddedServiceDesc">{svc.description}</span>}
                                </div>
                                <span className="pdAddedServicePrice">
                                  {parseFloat(svc.price) > 0 ? `+${formatCurrency(svc.price, currency, exchangeRate)}` : 'Free'}
                                  {parseFloat(svc.price) > 0 && (
                                    <span style={{ fontSize: '11px', color: '#888', display: 'block' }}>
                                      {svc.pricingMode === 'perUnit' ? 'per unit' : 'per order'}
                                    </span>
                                  )}
                                </span>
                              </label>
                              {/* perOrder: no qty controls — 1 service included per order */}
                              {isServiceSelected(idx) && svc.pricingMode === 'perOrder' && parseFloat(svc.price) > 0 && (
                                <div className="pdAddedServiceQtyRow">
                                  <span className="pdAddedServiceQtyLabel">Included: 1 per order</span>
                                  <span className="pdAddedServiceSubtotal">{formatCurrency(parseFloat(svc.price), currency, exchangeRate)}</span>
                                </div>
                              )}
                              {/* perUnit: show qty controls; qty defaults to product qty, buyer can change freely */}
                              {isServiceSelected(idx) && svc.pricingMode === 'perUnit' && parseFloat(svc.price) > 0 && (
                                <div className="pdAddedServiceQtyRow">
                                  <span className="pdAddedServiceQtyLabel">Quantity:</span>
                                  <div className="pdAddedServiceQtyControls">
                                    <button type="button" className="pdAddedServiceQtyBtn" onClick={() => updateAddedServiceQty(idx, getServiceQty(idx) - 1)} disabled={getServiceQty(idx) <= 1}>−</button>
                                    <span className="pdAddedServiceQtyDisplay">{getServiceQty(idx)}</span>
                                    <button type="button" className="pdAddedServiceQtyBtn" onClick={() => updateAddedServiceQty(idx, getServiceQty(idx) + 1)}>+</button>
                                  </div>
                                  <span className="pdAddedServiceSubtotal">{formatCurrency(parseFloat(svc.price) * getServiceQty(idx), currency, exchangeRate)}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Delivery */}
                      <div className="deliveryOptions">
                        <p className="deliveryLabel">Delivery</p>
                        <label className="deliveryOption">
                          <input type="radio" name="deliveryOption" value="delivery" checked={selectedDeliveryType === 'delivery'} readOnly />
                          <span>Delivery</span>
                          {computeDeliveryFee('delivery') > 0 && (
                            <span className="pdDeliveryFeeInline">+{formatCurrency(computeDeliveryFee('delivery'), currency, exchangeRate)}</span>
                          )}
                          {computeDeliveryFee('delivery') === 0 && (
                            <span className="pdFreeShippingInline">Free</span>
                          )}
                        </label>
                      </div>

                      {/* Est. shipment */}
                      {b2bListing.shippingTimeDays && (
                        <p className="deliveryInfo">
                          <FaTruck style={{ marginRight: 4 }} /> Est. shipment: {b2bListing.shippingTimeDays} days
                        </p>
                      )}

                      {product.cancellable === 'no' && (
                        <p className="cancellableDisclaimer"><FaShieldAlt style={{ marginRight: 4 }} /> Orders cannot be cancelled once confirmed</p>
                      )}

                      {/* Total */}
                      {(addedServicesTotal > 0 || quantity > 1 || getVariableOptionsPremium() > 0) && (
                        <div className="pdTotalRow">
                          <span>Total</span>
                          <span className="pdTotalPrice">{formatCurrency(totalPrice, currency, exchangeRate)}</span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Customization Content */}
                  {(b2bListing.listingMode !== 'wholesale' && (b2bListing.listingMode !== 'both' || b2bSidebarTab === 'customization')) && (
                    <>
                      <div className="priceInfo">
                        <p className="priceLabel">Custom Orders</p>
                        <p className="variableBrief">This supplier offers customization for this {b2bListing.listingType === 'service' ? 'service' : 'product'}. Contact them to discuss your requirements.</p>
                      </div>

                      {b2bListing.customizationOptions?.length > 0 && b2bListing.customizationOptions.map((opt, idx) => (
                        <div key={idx} style={{ padding: '12px', background: '#f9fafb', borderRadius: '10px', marginBottom: '8px', border: '1px solid #e5e7eb' }}>
                          <p style={{ fontWeight: 600, fontSize: '14px', marginBottom: '6px' }}>{opt.description}</p>
                          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#6b7280' }}>
                            {opt.moq > 0 && <span>MOQ: {opt.moq} pcs</span>}
                            {opt.samplePrice > 0 && <span>Sample: {formatCurrency(opt.samplePrice, currency, exchangeRate)}</span>}
                            {opt.sampleShippingFee > 0 && <span>Sample shipping: {formatCurrency(opt.sampleShippingFee, currency, exchangeRate)}</span>}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </>
              ) : (
                <>
                  {/* Package Selector */}
                  {product.pricingType === 'fixed' && (
                    <div className="packageOptions">
                      <p className="packageOptionsLabel">Select Package</p>
                      <div className="packageOptionsGrid">
                        {product.packages.map((pkg, idx) => (
                          <button key={idx} className={`packageOption ${selectedPackage === idx ? 'active' : ''}`} onClick={() => handlePackageSelect(idx)}>
                            {pkg.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Price Section */}
                  <div className="priceInfo">
                    {product.pricingType === 'fixed' ? (
                      <p className="priceLabel">{product.packages[selectedPackage]?.name} — {product.packages[selectedPackage]?.subtitle}</p>
                    ) : (
                      <p className="priceLabel">Price per unit</p>
                    )}

                    <div className="pdPriceRow">
                      <h2 className="priceValue">{formatCurrency(unitPrice, currency, exchangeRate)}</h2>
                      {saleDiscountActive && unitPrice < basePrice && (
                        <span className="pdOriginalPrice">{formatCurrency(basePrice, currency, exchangeRate)}</span>
                      )}
                      {saleDiscountActive && (
                        <span className="pdDiscountBadge">
                          {saleDiscountActive.type === 'percentage' ? `${saleDiscountActive.value}% OFF` : `$${saleDiscountActive.value} OFF`}
                        </span>
                      )}
                    </div>

                    {saleDiscountActive?.endDate && (
                      <p className="pdSaleEndsSidebar">Sale ends {new Date(saleDiscountActive.endDate).toLocaleDateString()}</p>
                    )}
                  </div>

                  {/* Package Description & Offerings */}
                  {product.pricingType === 'fixed' && product.packages[selectedPackage]?.briefDescription && (
                    <p className="variableBrief">{product.packages[selectedPackage].briefDescription}</p>
                  )}
                  {product.pricingType === 'fixed' && product.packages[selectedPackage]?.options?.offerings?.length > 0 && (
                    <div className="pdOfferingsSection">
                      <p className="pdOfferingsTitle">Package Includes</p>
                      <ul className="packageFeatures">
                        {product.packages[selectedPackage].options.offerings.map((o, i) => (
                          <li key={i}> {o.description}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Variable Description & Selectors */}
                  {product.pricingType === 'variable' && product.variablePricing?.briefDescription && (
                    <p className="variableBrief">{product.variablePricing.briefDescription}</p>
                  )}
                  {product.pricingType === 'variable' && product.variablePricing?.variables?.map((variable, idx) => (
                    <div key={idx} className="variableSelectorWrapper">
                      <p className="variableLabel">{variable.name}</p>
                      <div className="visualOptionsGrid">
                        {variable.options.map((opt, i) => {
                          const isSelected = selectedVariables[variable.name] === opt.name;
                          return (
                            <div key={i} className={`visualOptionChip ${isSelected ? 'active' : ''}`}
                              onClick={() => { handleVariableChange(variable.name, opt.name); setCustomMainImage(opt.image || null); }}
                            >
                              {opt.image && <img src={opt.image} alt={opt.name} className="chipImage" />}
                              <div className="chipDetails">
                                <span className="chipName">{opt.name}</span>
                                <span className="chipPrice">{opt.price > 0 ? `+${formatCurrency(opt.price, currency, exchangeRate)}` : 'Included'}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Quantity */}
                  <div className="quantitySelector">
                    <span className="quantityLabel">{product.listingType === 'service' ? 'Sessions / Units' : 'Quantity'}</span>
                    <div className="quantityControls">
                      <button className="quantityBtn" onClick={() => quantity > 1 && setQuantity(quantity - 1)} disabled={quantity <= 1}>−</button>
                      <input
                        type="text"
                        className="quantityInput"
                        value={quantity}
                        onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setQuantity(v === '' ? '' : parseInt(v)); }}
                        onBlur={() => { const max = product.stock != null ? product.stock : Infinity; setQuantity(prev => { const n = parseInt(prev); return (!n || n < 1) ? 1 : (n > max ? max : n); }); }}
                      />
                      <button className="quantityBtn" onClick={() => setQuantity(quantity + 1)} disabled={isOutOfStock || (product.stock != null && quantity >= product.stock)}>+</button>
                    </div>
                  </div>

                  {/* Bulk pricing hint in sidebar */}
                  {product.bulkPricing?.length > 0 && product.listingType !== 'service' && (() => {
                    const sorted = [...product.bulkPricing]
                      .filter(t => product.pricingType !== 'fixed' || !t.packageName || t.packageName === product.packages[selectedPackage]?.name)
                      .sort((a, b) => a.minQuantity - b.minQuantity);
                    const nextTier = sorted.find(t => quantity < t.minQuantity);
                    if (!nextTier) return null;
                    const tierPrice = getEffectiveUnitPrice(nextTier.minQuantity);
                    const currentPrice = getEffectiveUnitPrice(quantity);
                    const savings = currentPrice > 0 ? Math.round((1 - tierPrice / currentPrice) * 100) : 0;
                    return (
                      <p className="pdBulkHint">
                        <FaTag /> Buy {nextTier.minQuantity}+ for {formatCurrency(tierPrice, currency, exchangeRate)} each
                        {savings > 0 && <span style={{ marginLeft: 6, fontWeight: 600, color: '#16a34a' }}>({savings}% off)</span>}
                      </p>
                    );
                  })()}

                  {/* Added Services */}
                  {product.addedServices?.length > 0 && (
                    <div className="pdAddedServices">
                      <p className="pdAddedServicesTitle">Added Services</p>
                      {product.addedServices.map((svc, idx) => (
                        <div key={idx} className={`pdAddedServiceItem ${isServiceSelected(idx) ? 'active' : ''}`}>
                          <label className="pdAddedServiceMain">
                            <input type="checkbox" checked={isServiceSelected(idx)} onChange={() => toggleAddedService(idx)} />
                            {svc.image && <img src={svc.image} alt={svc.name} className="pdAddedServiceImg" />}
                            <div className="pdAddedServiceInfo">
                              <span className="pdAddedServiceName">{svc.name}</span>
                              {svc.description && <span className="pdAddedServiceDesc">{svc.description}</span>}
                            </div>
                            <span className="pdAddedServicePrice">
                              {parseFloat(svc.price) > 0 ? `+${formatCurrency(svc.price, currency, exchangeRate)}` : 'Free'}
                              {parseFloat(svc.price) > 0 && (
                                <span style={{ fontSize: '11px', color: '#888', display: 'block' }}>
                                  {svc.pricingMode === 'perUnit' ? 'per unit' : 'per order'}
                                </span>
                              )}
                            </span>
                          </label>
                          {/* perOrder: no qty controls — 1 service included per order */}
                          {isServiceSelected(idx) && svc.pricingMode === 'perOrder' && parseFloat(svc.price) > 0 && (
                            <div className="pdAddedServiceQtyRow">
                              <span className="pdAddedServiceQtyLabel">Included: 1 per order</span>
                              <span className="pdAddedServiceSubtotal">{formatCurrency(parseFloat(svc.price), currency, exchangeRate)}</span>
                            </div>
                          )}
                          {/* perUnit: show qty controls; qty defaults to product qty, buyer can change freely */}
                          {isServiceSelected(idx) && svc.pricingMode === 'perUnit' && parseFloat(svc.price) > 0 && (
                            <div className="pdAddedServiceQtyRow">
                              <span className="pdAddedServiceQtyLabel">Quantity:</span>
                              <div className="pdAddedServiceQtyControls">
                                <button type="button" className="pdAddedServiceQtyBtn" onClick={() => updateAddedServiceQty(idx, getServiceQty(idx) - 1)} disabled={getServiceQty(idx) <= 1}>−</button>
                                <span className="pdAddedServiceQtyDisplay">{getServiceQty(idx)}</span>
                                <button type="button" className="pdAddedServiceQtyBtn" onClick={() => updateAddedServiceQty(idx, getServiceQty(idx) + 1)}>+</button>
                              </div>
                              <span className="pdAddedServiceSubtotal">{formatCurrency(parseFloat(svc.price) * getServiceQty(idx), currency, exchangeRate)}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Delivery Options */}
                  {product.listingType === 'service' ? (
                    <div className="deliveryOptions">
                      <p className="deliveryLabel">Service Mode</p>
                      {product.serviceMode === 'online' && <p className="deliveryInfo">This is an online/digital service</p>}
                      {product.serviceMode === 'at_seller_location' && product.pickupLocation && <p className="deliveryInfo"><FaMapMarkerAlt style={{ marginRight: 4 }} /> Service at: {product.pickupLocation} <span className="pdPickupNote">(exact address shared after payment)</span></p>}
                      {product.serviceMode === 'onsite' && <p className="deliveryInfo"><FaMapMarkerAlt style={{ marginRight: 4 }} /> Service will be provided at your location</p>}
                    </div>
                  ) : (
                    <div className="deliveryOptions">
                      <p className="deliveryLabel">Delivery</p>
                      {product.deliveryType.map(type => (
                        <label key={type} className="deliveryOption">
                          <input type="radio" name="deliveryOption" value={type} checked={selectedDeliveryType === type} onChange={() => setSelectedDeliveryType(type)} />
                          <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                          {type === 'delivery' && computeDeliveryFee(type) > 0 && (
                            <span className="pdDeliveryFeeInline">+{formatCurrency(computeDeliveryFee(type), currency, exchangeRate)}</span>
                          )}
                          {type === 'delivery' && computeDeliveryFee(type) === 0 && (
                            <span className="pdFreeShippingInline">Free</span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}

                  {(product.listingType !== 'service' || product.serviceMode !== 'online') && (
                    <>
                      {selectedDeliveryType === 'delivery' && (
                        <p className="deliveryInfo">
                          <FaTruck style={{ marginRight: 4 }} /> Est. shipment: {product.pricingType === 'fixed'
                            ? product.packages[selectedPackage]?.options?.timeToDeliver
                            : product.variablePricing?.timeToDeliver}
                        </p>
                      )}
                      {selectedDeliveryType === 'pickup' && (
                        <p className="deliveryInfo">
                          <FaMapMarkerAlt style={{ marginRight: 4 }} /> Ready in: {product.pricingType === 'fixed'
                            ? product.packages[selectedPackage]?.options?.timeframe || product.packages[selectedPackage]?.options?.timeToDeliver
                            : product.variablePricing?.timeframe || product.variablePricing?.timeToDeliver}
                          {product.pickupLocation && <span className="pdPickupAddr"> · {product.pickupLocation} <span className="pdPickupNote">(exact address shared after payment)</span></span>}
                        </p>
                      )}
                    </>
                  )}

                  {product.cancellable === 'no' && (
                    <p className="cancellableDisclaimer"><FaShieldAlt style={{ marginRight: 4 }} /> This order cannot be cancelled once placed</p>
                  )}

                  {/* Total */}
                  {(addedServicesTotal > 0 || quantity > 1 || getVariableOptionsPremium() > 0) && (
                    <div className="pdTotalRow">
                      <span>Total</span>
                      <span className="pdTotalPrice">{formatCurrency(totalPrice, currency, exchangeRate)}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="productDetailSidebarFooter">
              {isB2B ? (
                <div className="actionButtons">
                  {showB2BBuyNow && (
                    <>
                      <button className="primaryBtn buyNowBtn" onClick={handleBuyNow}>
                        {isServiceListing ? 'Book Now' : 'Buy Now'}
                      </button>
                      {!isServiceListing && (isInCart ? (
                        <button
                          type="button"
                          className="primaryBtn3 removeFromCartBtn"
                          onClick={() => {
                            setIsInCart(false);
                            removeFromCart(product.id, cartItem?._id);
                          }}
                        >
                          Remove from Cart
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="primaryBtn3 addToCartBtn"
                          onClick={handleAddToCart}
                          disabled={cartActionLoading}
                        >
                          Add to Cart
                        </button>
                      ))}
                    </>
                  )}
                  {b2bCustomizationActive && (
                    <button
                      type="button"
                      className="primaryBtn3 addToCartBtn"
                      onClick={() => {
                        if (!isLogin) {
                          navigate('/signin', { state: { returnTo: `/productDetail/b2b/${id}` } });
                          return;
                        }
                        openFloatingChat(b2bListing.b2bSellerId?.userId?._id || b2bListing.b2bSellerId?.userId);
                      }}
                    >
                      <FaComment style={{ marginRight: 6 }} /> Contact Supplier
                    </button>
                  )}
                </div>
              ) : (
                <div className="actionButtons">
                  <button className="primaryBtn buyNowBtn" onClick={handleBuyNow} disabled={isOutOfStock}>
                    {isOutOfStock ? 'Out of Stock' : isServiceListing ? 'Book Now' : 'Buy Now'}
                  </button>
                  {!isServiceListing && (isInCart ? (
                    <button className="primaryBtn3 removeFromCartBtn" onClick={() => { setIsInCart(false); removeFromCart(id, cartItem?._id); }}>
                      Remove from Cart
                    </button>
                  ) : (
                    <button className="primaryBtn3 addToCartBtn" onClick={handleAddToCart} disabled={isOutOfStock}>
                      Add to Cart
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Full-width suggested postings from platform (no sidebar) — below reviews */}
      {product && (
        <div className="productDetailRelatedFull">
          <div className="productDetailRelatedFullInner">
            <h2 className="productDetailRelatedFullTitle"><FaShoppingBag /> Recommended for you</h2>
            <p className="productDetailRelatedFullSubtitle">Similar listings from across the platform</p>
            {suggestedLoading && suggestedProducts.length === 0 ? (
              <Loader type="simpleMini" />
            ) : suggestedProducts.length === 0 ? (
              <p className="productDetailSuggestedEmpty">No similar listings at the moment. Check back later.</p>
            ) : (
              <>
                <div className="productDetailRelatedGrid">
                  {isB2B
                    ? suggestedProducts.map((listing) => (
                        <ProductCard key={listing._id} b2bListing={listing} />
                      ))
                    : suggestedProducts.map((p) => (
                        <ProductCard key={p.id} product={p} />
                      ))}
                </div>
                {suggestedHasMore && (
                  <div className="productDetailRelatedMoreWrap">
                    <button type="button" className="productDetailRelatedMoreBtn" onClick={loadMoreSuggested} disabled={suggestedLoading}>
                      {suggestedLoading ? 'Loading...' : 'View more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
